import React, { ChangeEvent, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import InputField from '../../../form/inputField';
import DropDownField from '../../../form/dropdownField';
import getIssuers from '../../../../helpers/issuers';
import i18n from 'i18next';
import { IIssuerModel } from '../../../../helpers/models/issuerModel';
import { IAccountModel } from '../../../../helpers/models/accountModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { ProfessionalStep2FormResultKeys } from './professionalStep2FormModel';
import { IRegulationModel } from '../../../../helpers/models/regulationModel';
import getRegulations from '../../../../helpers/regulations';
import FormIssuerModal, { IFormIssuerRef } from '../../../modal/instances/formIssuerModal';
import {
    getDateMaximumString,
    getDateMinimumString,
    handleCustomValidityOfDateFieldWithMinMax
} from '../../../form/dateField';

export interface IProfessionalStep2LicenseProps extends WithTranslation {
    account: IAccountModel;
    provider?: number;
    readonly?: boolean;
}

const ProfessionalStep2License = (props: IProfessionalStep2LicenseProps) => {
    const { t, provider, readonly, account } = props;

    const issuerComponents: JSX.Element[] = [];
    const [issuers, setIssuers] = useState<IIssuerModel[]>([]);
    const [regulations, setRegulations] = useState<IRegulationModel[]>([]);
    const [selectedIssuer, setSelectedIssuer] = useState<number>();
    const [issueDate, setIssueDate] = useState<string>(
        account.provider.length > 0 && account.provider[0]?.licenceDate
            ? new Date(account.provider[0]?.licenceDate).toISOString().split('T')[0]
            : ''
    );
    const ref = React.createRef<IFormIssuerRef>();

    useEffect(() => {
        const regulation = regulations.filter((element: IRegulationModel) => {
            return element.issuerId === selectedIssuer;
        });

        if (regulation.length) {
            ref.current?.setModalOpen(true, regulation.at(0)!.text, regulation.at(0)!.title);
        } else {
            ref.current?.setModalOpen(false);
        }
    }, [selectedIssuer]);

    useEffect(() => {
        async function getData() {
            const issuerList = provider
                ? await getIssuers(
                      i18n.language,
                      Number(provider),
                      +(account.province as IProvinceModel).id
                  )
                : [];
            setIssuers(issuerList ?? []);

            const regulationList = await getRegulations(
                i18n.language,
                +(account!.province as IProvinceModel).id
            );
            setRegulations(regulationList ?? []);
        }
        getData();

        const issueDateField = (document.getElementById(ProfessionalStep2FormResultKeys.ISSUE_DATE) as HTMLInputElement)!;
        handleCustomValidityOfDateFieldWithMinMax(t, issueDateField, 'form_date_minimum_error', 'form_date_maximum_error');
    }, [i18n.language, provider]);

    const handleOnChangeIssuer = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedIssuer(+e.target.value);
    };

    issuers
        ?.filter((issuer: IIssuerModel) => {
            return issuer.role.id === provider?.toString();
        })
        .forEach((issuer: IIssuerModel) => {
            issuerComponents.push(
                <option key={issuer.name} value={issuer.id}>
                    {issuer.name}
                </option>
            );
        });

    return (
        <>
            <FormIssuerModal ref={ref} />
            <h2 className="header-3 header-3--purple registration_form_header">
                {t('form_professional_step_2_provider_license_title')}
            </h2>
            <div className="registration-form-container">
                <div className="registration-section">
                    {issuerComponents.length > 1 ? (
                        <DropDownField
                            fieldContainerClassName="registration-field"
                            fieldName={ProfessionalStep2FormResultKeys.ISSUER}
                            fieldTitle="form_professional_step_2_issuer"
                            fieldOptions={issuerComponents}
                            handleOnChange={handleOnChangeIssuer}
                            fieldDisabled={readonly}
                            fieldRequired
                            fieldHiddenLabel="form_professional_step_2_provider_license_title"
                            fieldWeight={7}
                            value={
                                account.provider.length
                                    ? (account.provider[0]?.issuer as IIssuerModel).id ?? ''
                                    : ''
                            }
                        />
                    ) : (
                        <div className={'field-container registration-field'}>
                            <label
                                htmlFor="form_professional_step_2_issuer"
                                className={'field-label field-label-container '.concat(
                                    readonly ? 'field-label--disabled' : ''
                                )}
                            >
                                <>{t('form_professional_step_2_issuer')}</>
                            </label>
                            <select
                                id="form_professional_step_2_issuer"
                                name={ProfessionalStep2FormResultKeys.ISSUER}
                                className="field-input field-input--dropdown field-value"
                                disabled
                                defaultValue={
                                    issuerComponents.length ? issuerComponents[0].props.value : ''
                                }
                                data-set-weight={7}
                            >
                                <option
                                    value={
                                        issuerComponents.length
                                            ? issuerComponents[0].props.value
                                            : ''
                                    }
                                    disabled
                                >
                                    {issuerComponents.length ? issuerComponents[0].key : ''}
                                </option>
                            </select>
                        </div>
                    )}
                </div>
                <div className="registration-section">
                    <div className="registration-column-container">
                        <div className="registration-column-item">
                            <InputField
                                fieldContainerClassName="registration-field"
                                fieldName={ProfessionalStep2FormResultKeys.NUMBER}
                                fieldTitle="form_professional_step_2_number"
                                fieldDisabled={readonly}
                                fieldHiddenLabel="form_professional_step_2_provider_license_title"
                                defaultValue={
                                    account.provider.length
                                        ? account.provider[0]?.licenceNumber ?? 0
                                        : ''
                                }
                                type="text"
                                fieldRequired
                                fieldWeight={8}
                            />
                        </div>
                        <div className="registration-column-item">
                            <InputField
                                fieldContainerClassName="registration-field"
                                fieldName={ProfessionalStep2FormResultKeys.ISSUE_DATE}
                                fieldTitle="form_professional_step_2_issue_date"
                                fieldDisabled={readonly}
                                type="date"
                                min={getDateMinimumString()}
                                max={getDateMaximumString()}
                                fieldInputClass="field-date"
                                fieldHiddenLabel="form_professional_step_2_provider_license_title"
                                defaultValue={issueDate}
                                onChange={(e) => setIssueDate(e.target.value)}
                                fieldWeight={9}
                                fieldRequired
                                handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    handleCustomValidityOfDateFieldWithMinMax(t, e.target, 'form_date_minimum_error', 'form_date_maximum_error')
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default withTranslation()(ProfessionalStep2License);
